import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";

interface SelectOption {
  value: string;
  label: string;
  level?: number;
  isRoot?: boolean;
}

interface CustomSelectProps {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  loading?: boolean;
  searchable?: boolean;
}

export function CustomSelect({
  options,
  value,
  onChange,
  placeholder = "Selecciona una opción",
  disabled = false,
  error = false,
  loading = false,
  searchable = true,
}: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filtrar opciones basado en búsqueda (solo si es searchable)
  const filteredOptions = searchable 
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Encontrar la opción seleccionada
  const selectedOption = options.find(option => option.value === value);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Manejar selección
  const handleSelect = (option: SelectOption) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm("");
  };

  // Manejar teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Input trigger */}
      <div
        className={`relative w-full px-3 py-2 border rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#282828] focus:border-[#282828] cursor-pointer transition-colors ${
          error
            ? "border-red-300 focus:ring-red-500 focus:border-red-500"
            : "border-gray-300 hover:border-gray-400"
        } ${disabled ? "bg-gray-100 cursor-not-allowed" : ""}`}
        onClick={(e) => {
          e.preventDefault();
          if (!disabled && !loading) {
            setIsOpen(!isOpen);
            if (!isOpen) {
              // Pequeño delay para que el input esté listo para focus
              setTimeout(() => {
                inputRef.current?.focus();
              }, 0);
            }
          }
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={isOpen && searchable ? searchTerm : (selectedOption?.label || "")}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={loading ? "Cargando..." : placeholder}
          disabled={disabled || loading}
          className="w-full bg-transparent border-none outline-none cursor-pointer disabled:cursor-not-allowed"
          readOnly={!isOpen || !searchable}
          autoComplete="off"
        />
        <ChevronDown
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 transition-transform pointer-events-none ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No se encontraron opciones
            </div>
          ) : (
            filteredOptions.map((option) => {
              return (
              <div
                key={option.value}
                onClick={() => handleSelect(option)}
                className={`px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 ${
                  option.value === value ? "bg-[#282828]/10" : ""
                }`}
                style={{
                  paddingLeft: option.level && option.level > 0 ? `${12 + option.level * 16}px` : '12px',
                }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-sm ${
                      option.isRoot ? "font-semibold text-gray-900" : "text-gray-700"
                    }`}
                  >
                    {option.label}
                  </span>
                  {option.value === value && (
                    <Check className="h-4 w-4 text-[#282828]" />
                  )}
                </div>
                {(option.level ?? 0) > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Subcategoría
                  </div>
                )}
              </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Alias para mantener compatibilidad
export const CategorySelect = CustomSelect;
